import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useParentChildren } from "@/hooks/useParentChildren";
import ChildSelector from "@/components/ChildSelector";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, BookOpenCheck, CalendarClock, Trash2, Sparkles } from "lucide-react";

export default function HomeworkPage() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const { selected: selectedChild } = useParentChildren();

  const [hw, setHw] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ class_id: "", subject: "", title: "", description: "", due_date: "" });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [mySubmissions, setMySubmissions] = useState({});

  const isTeacher = ["admin", "principal", "teacher"].includes(user?.role);
  const isStudent = user?.role === "student";

  const load = async () => {
    const params = isParent && selectedChild?.class_id ? { class_id: selectedChild.class_id } : {};
    const { data } = await api.get("/homework", { params });
    setHw(data);
    if (isStudent) {
      const { data: subs } = await api.get("/homework/mine/submissions");
      const map = {};
      subs.forEach((s) => { map[s.homework_id] = s; });
      setMySubmissions(map);
    }
  };

  useEffect(() => {
    load();
    api.get("/classes").then((r) => setClasses(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChild?.id]);

  const create = async () => {
    try {
      await api.post("/homework", form);
      toast.success("Homework created");
      setOpen(false);
      setForm({ class_id: "", subject: "", title: "", description: "", due_date: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/homework/${id}`);
    toast.success("Deleted");
    load();
  };

  const submit = async (h) => {
    const content = prompt(`Submit homework for "${h.title}":`);
    if (!content) return;
    await api.post("/homework/submit", { homework_id: h.id, content });
    toast.success("Submitted");
    load();
  };

  const aiHint = async (h) => {
    setAiOpen(true);
    setAiTopic(`${h.subject}: ${h.title}`);
    setAiResult("");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/generate", {
        kind: "homework_hint", subject: h.subject, topic: h.title, grade: user?.grade || "8",
      });
      setAiResult(data.text);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="homework-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Learning</div>
          <h1 className="text-4xl font-bold tracking-tight">Homework</h1>
          <p className="text-muted-foreground text-sm mt-1">Assignments, submissions and AI-powered hints for learners.</p>
        </div>
        {isTeacher && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-2xl gap-2" data-testid="hw-create-open"><Plus className="h-4 w-4" /> New homework</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Assign homework</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                  <SelectTrigger className="rounded-2xl" data-testid="hw-class"><SelectValue placeholder="Class" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-2xl" data-testid="hw-subject" />
                <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-2xl" data-testid="hw-title" />
                <Textarea placeholder="Description / instructions" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-2xl" />
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-2xl" data-testid="hw-due" />
              </div>
              <DialogFooter><Button onClick={create} className="rounded-2xl" data-testid="hw-create-submit">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <ChildSelector />

      <div className="grid gap-4 md:grid-cols-2">
        {hw.length === 0 && <div className="col-span-full text-center py-16 text-sm text-muted-foreground">No homework assigned yet.</div>}
        {hw.map((h) => (
          <Card key={h.id} className="rounded-2xl lift" data-testid={`hw-${h.id}`}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">{h.subject}</div>
                <CardTitle className="text-lg mt-1">{h.title}</CardTitle>
              </div>
              <Badge variant="outline" className="rounded-full"><CalendarClock className="h-3 w-3 mr-1" /> {h.due_date}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{h.description}</p>
              <div className="mt-4 flex gap-2 flex-wrap">
                {isStudent && (
                  <>
                    <Button size="sm" onClick={() => submit(h)} className="rounded-xl" data-testid={`hw-submit-${h.id}`}>
                      {mySubmissions[h.id] ? "Resubmit" : "Submit"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => aiHint(h)} className="rounded-xl gap-1" data-testid={`hw-ai-${h.id}`}>
                      <Sparkles className="h-3.5 w-3.5" /> AI Hint
                    </Button>
                    {mySubmissions[h.id]?.grade && (
                      <Badge className="rounded-full">Grade: {mySubmissions[h.id].grade}</Badge>
                    )}
                  </>
                )}
                {isTeacher && (
                  <Button size="sm" variant="outline" onClick={() => remove(h.id)} className="rounded-xl gap-1 text-red-500" data-testid={`hw-delete-${h.id}`}>
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> AI Hint · {aiTopic}</DialogTitle></DialogHeader>
          <div className="min-h-32 p-4 rounded-xl bg-muted/40 whitespace-pre-wrap text-sm leading-relaxed">
            {aiLoading ? "Thinking…" : (aiResult || "No hint yet.")}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
